import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Minus, Play, BookOpen, Timer, GraduationCap, AlertCircle, Loader2, ListOrdered, Clock, HelpCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import QuizGenerationLoader from "./QuizGenerationLoader";
import { useAuth } from "@/contexts/AuthContext";

type Difficulty = "easy" | "medium" | "hard" | "mix";
type Penalty = -0.25 | -0.5 | -0.75 | -1.0;

const questionSchema = z.number().min(1).max(50);

interface QuizGeneratorProps {
  onHowToUseClick?: () => void;
}

const QuizGenerator = ({ onHowToUseClick }: QuizGeneratorProps) => {
  const [topic, setTopic] = useState("");
  const [numQuestions, setNumQuestions] = useState(0);
  const [timer, setTimer] = useState(0);
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [negativeMarking, setNegativeMarking] = useState(false);
  const [penalty, setPenalty] = useState<Penalty>(-0.25);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, session } = useAuth();

  const handleNumQuestionsChange = (value: number) => {
    if (isNaN(value) || value === 0) {
      setNumQuestions(0);
      setTimer(0);
      return;
    }
    const validatedValue = Math.min(Math.max(value, 1), 50);
    setNumQuestions(validatedValue);
    // Auto-calculate timer: 1 minute per question
    setTimer(validatedValue);
  };

  const adjustTimer = (delta: number) => {
    const newValue = timer + delta;
    if (newValue >= 1 && newValue <= 300) {
      setTimer(newValue);
    }
  };


  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast({
        title: "Topic Required",
        description: "Please enter a topic for your quiz",
        variant: "destructive",
      });
      return;
    }

    if (numQuestions < 1 || numQuestions > 50) {
      toast({
        title: "Invalid Number",
        description: "Number of questions must be between 1 and 50",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      
      // Use session token if user is authenticated
      const authToken = session?.access_token || supabaseKey;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-quiz`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          topic: topic.trim(),
          numQuestions,
          difficulty,
          negativeMarking,
          penalty: negativeMarking ? penalty : 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === 'rate_limit_exceeded') {
          // Only show signup message if user is NOT authenticated
          if (!user) {
            toast({
              title: "Free Limit Reached",
              description: "You've used all 5 free quiz generations. Sign up now for FREE to create unlimited quizzes! Signing up is completely free and required to continue.",
              variant: "destructive",
              duration: 10000,
              className: "bottom-0 left-0 right-0 m-0 rounded-none border-0 sm:bottom-4 sm:left-auto sm:right-4 sm:max-w-[500px] sm:rounded-2xl sm:border sm:m-0",
              action: (
                <div className="flex flex-col sm:flex-row gap-2 mt-3 w-full sm:w-auto">
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={() => navigate("/auth?mode=signup")}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold w-full sm:w-auto h-10"
                  >
                    Sign Up Free
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate("/auth?mode=signin")}
                    className="bg-background hover:bg-muted text-foreground font-semibold w-full sm:w-auto border-2 h-10"
                  >
                    Sign In
                  </Button>
                </div>
              ),
            });
          } else {
            // Authenticated users should not hit this, but show a generic error
            toast({
              title: "Error",
              description: "Failed to generate quiz. Please try again.",
              variant: "destructive",
            });
          }
          setIsGenerating(false);
          return;
        }
        throw new Error(errorData.error || 'Failed to generate quiz');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let allContent = '';

      if (!reader) {
        throw new Error('No response stream available');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || trimmedLine === 'data: [DONE]') continue;
          if (trimmedLine.startsWith('data: ')) {
            const jsonStr = trimmedLine.slice(6);
            try {
              const parsed = JSON.parse(jsonStr);
              if (parsed.content) {
                allContent += parsed.content;
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }

      // Final parse of complete data
      const finalQuizData = JSON.parse(allContent);
      
      if (!finalQuizData.questions || !Array.isArray(finalQuizData.questions)) {
        throw new Error('Invalid quiz data structure');
      }

      toast({
        title: "Quiz Generated!",
        description: `Created ${finalQuizData.questions.length} questions. Starting quiz...`,
      });

      // Navigate to quiz page with data
      setTimeout(() => {
        navigate("/quiz", {
          state: {
            quizData: {
              questions: finalQuizData.questions,
              settings: {
                topic: topic.trim(),
                difficulty,
                negativeMarking,
                penalty: negativeMarking ? penalty : 0,
                totalQuestions: numQuestions,
                timerMinutes: timer,
              },
            },
          },
        });
      }, 500);
    } catch (error: any) {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate quiz. Please try again.",
        variant: "destructive",
      });
      setIsGenerating(false);
    }
  };

  const difficultyConfig = {
    easy: { color: "success", icon: "🟢" },
    medium: { color: "warning", icon: "🟡" },
    hard: { color: "destructive", icon: "🔴" },
    mix: { color: "primary", icon: "🎨" },
  };

  return (
    <div className="w-full mx-auto relative">
      {isGenerating && <QuizGenerationLoader topic={topic.trim()} />}
      <div className="bg-card rounded-2xl sm:rounded-3xl p-4 sm:p-5 md:p-6 shadow-card border border-border">
        {/* Topic Input */}
        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1.5 sm:space-x-2">
              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
              <Label htmlFor="topic" className="text-sm sm:text-base font-semibold">
                Topic / Subject
              </Label>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onHowToUseClick}
              className="text-xs sm:text-sm hover:bg-primary hover:text-primary-foreground rounded-full px-3 sm:px-4 transition-smooth flex items-center gap-1.5"
            >
              <HelpCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              How to Use
            </Button>
          </div>
          <Textarea
            id="topic"
            placeholder="Write detailed topic here... Example: JavaScript basics - variables, loops, functions, etc."
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="min-h-[100px] sm:min-h-[120px] resize-none rounded-xl sm:rounded-2xl border-2 focus:border-primary transition-smooth bg-background text-sm sm:text-base"
            disabled={isGenerating}
          />
          <p className="text-xs sm:text-sm text-muted-foreground">
            Provide detailed topic description for better questions
          </p>
        </div>

        {/* Number of Questions and Timer - Side by Side */}
        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <ListOrdered className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                <Label htmlFor="numQuestions" className="text-xs sm:text-base font-semibold">
                  <span className="hidden sm:inline">No. of Questions</span>
                  <span className="sm:hidden">Questions</span>
                </Label>
              </div>
              <Input
                id="numQuestions"
                type="number"
                min="1"
                max="50"
                value={numQuestions || ""}
                onChange={(e) => handleNumQuestionsChange(parseInt(e.target.value) || 0)}
                placeholder=""
                className="rounded-xl sm:rounded-2xl border-2 h-11 sm:h-12 text-center text-base sm:text-lg font-medium bg-background"
                disabled={isGenerating}
              />
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                <Label className="text-xs sm:text-base font-semibold">Timer</Label>
              </div>
              <div className="rounded-xl sm:rounded-2xl border-2 bg-background flex items-center justify-between px-2 sm:px-3 h-11 sm:h-12 w-full">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => adjustTimer(-1)}
                  className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 hover:bg-transparent"
                  disabled={isGenerating}
                  aria-label="Decrease timer by 1 minute"
                >
                  <Minus className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                </Button>
                <div className="flex items-baseline gap-1 sm:gap-1.5">
                  <label htmlFor="timer-input" className="sr-only">Timer in minutes</label>
                  <input
                    id="timer-input"
                    type="number"
                    min={1}
                    max={300}
                    value={timer || 0}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      if (value >= 1 && value <= 300) {
                        setTimer(value);
                      }
                    }}
                    className="w-10 sm:w-14 text-center text-base sm:text-lg font-semibold bg-transparent border-none focus:outline-none focus:ring-0 appearance-none"
                    disabled={isGenerating}
                    aria-describedby="timer-unit"
                  />
                  <span id="timer-unit" className="text-[11px] sm:text-xs text-muted-foreground">min</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => adjustTimer(1)}
                  className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 hover:bg-transparent"
                  disabled={isGenerating}
                  aria-label="Increase timer by 1 minute"
                >
                  <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground" role="note">
            <AlertCircle className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
            <span>Timer auto-adjusts: 1 min per question</span>
          </div>
        </div>

        {/* Difficulty Level */}
        <fieldset className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          <legend className="flex items-center space-x-1.5 sm:space-x-2 text-sm sm:text-base font-semibold">
            <GraduationCap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" aria-hidden="true" />
            <span>Difficulty Level</span>
          </legend>
          <div className="grid grid-cols-2 gap-2 sm:gap-3" role="radiogroup" aria-label="Select difficulty level">
            {(["easy", "medium", "hard", "mix"] as Difficulty[]).map((level) => (
              <Button
                key={level}
                variant={difficulty === level ? "default" : "outline"}
                onClick={() => setDifficulty(level)}
                disabled={isGenerating}
                role="radio"
                aria-checked={difficulty === level}
                className={`rounded-full h-10 sm:h-12 text-sm sm:text-base font-semibold transition-smooth ${
                  difficulty === level
                    ? level === "easy"
                      ? "bg-success hover:bg-success/90 text-success-foreground"
                      : level === "medium"
                      ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                      : level === "hard"
                      ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                      : "bg-dark-card hover:bg-dark-card/90 text-dark-card-foreground"
                    : "hover:bg-muted"
                }`}
              >
                <span className="mr-2" aria-hidden="true">{difficultyConfig[level].icon}</span>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </Button>
            ))}
          </div>
        </fieldset>

        {/* Negative Marking */}
        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" aria-hidden="true" />
            <Label className="text-sm sm:text-base font-semibold" id="negative-marking-label">Negative Marking</Label>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-3 p-2.5 sm:p-3 bg-muted/30 rounded-xl sm:rounded-2xl">
            <Switch
              id="negative-marking"
              checked={negativeMarking}
              onCheckedChange={setNegativeMarking}
              disabled={isGenerating}
              aria-labelledby="negative-marking-label"
            />
            <Label htmlFor="negative-marking" className="text-sm sm:text-base cursor-pointer">
              Enable Negative Marking
            </Label>
          </div>

          {negativeMarking && (
            <fieldset className="space-y-1.5 sm:space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <legend className="text-xs sm:text-sm text-muted-foreground">
                Penalty per Wrong Answer
              </legend>
              <div className="grid grid-cols-4 gap-1.5 sm:gap-2" role="radiogroup" aria-label="Select penalty amount">
                {([-0.25, -0.5, -0.75, -1.0] as Penalty[]).map((p) => (
                  <Button
                    key={p}
                    variant={penalty === p ? "default" : "outline"}
                    onClick={() => setPenalty(p)}
                    disabled={isGenerating}
                    role="radio"
                    aria-checked={penalty === p}
                    className={`rounded-full h-10 sm:h-12 text-sm sm:text-base font-semibold transition-smooth ${
                      penalty === p ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    }`}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </fieldset>
          )}
        </div>

        {/* Generate Button */}
        <Button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="w-full h-12 sm:h-14 rounded-full text-base sm:text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground transition-smooth shadow-card"
          aria-busy={isGenerating}
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" aria-hidden="true" />
              <span className="hidden sm:inline">Generating Quiz...</span>
              <span className="sm:hidden">Generating...</span>
            </>
          ) : (
            <>
              <Play className="mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
              Generate Quiz
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default QuizGenerator;

